/**
 * The one Panel presentation-format module: canonical ids,
 * basis points and durations. Money lives in `foundation/money.ts`.
 */

/** The one canonical UUID shape (versions 1–8, RFC variant) every Panel id check uses; the backend applies the same
 * rule (`backend/src/platform/contracts/typebox-formats.ts`).
 * Presentation-side validation only: the Backend stays the authority. */
export const CANONICAL_UUID_PATTERN = '[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';
export const CANONICAL_UUID = new RegExp(`^${CANONICAL_UUID_PATTERN}$`, 'i');

export const isCanonicalUuid = (value: unknown): value is string => typeof value === 'string' && CANONICAL_UUID.test(value);

/** Basis points as a Spanish percentage figure without the sign: 1250 → «12,5». */
export const basisPointsPercent = (basisPoints: number): string =>
  (basisPoints / 100).toLocaleString('es-ES', { maximumFractionDigits: 2 });

/** «12,5 %», or the screen's missing-value word. */
export const formatBasisPoints = (basisPoints: number | null, missing = '—'): string =>
  basisPoints === null ? missing : `${basisPointsPercent(basisPoints)} %`;

const decimalMilliseconds = (milliseconds: number): string =>
  milliseconds ? `,${String(milliseconds).padStart(3, '0').replace(/0+$/, '')}` : '';

/**
 * A measured duration in Spanish, exact to the millisecond the owner sent:
 * «850 ms», «12,5 s», «3 min 20 s», «1 h 5 min». Null (or a negative or
 * fractional amount, which no owner sends) is the screen's missing-value word.
 */
export function formatDuration(milliseconds: number | null, missing = '—'): string {
  if (milliseconds === null || !Number.isSafeInteger(milliseconds) || milliseconds < 0) return missing;
  if (milliseconds < 1_000) return `${milliseconds.toLocaleString('es-ES')} ms`;
  const totalSeconds = Math.floor(milliseconds / 1_000);
  const remainder = milliseconds % 1_000;
  if (totalSeconds < 60) return `${totalSeconds.toLocaleString('es-ES')}${decimalMilliseconds(remainder)} s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const remainderMinutes = minutes % 60;
    return `${hours.toLocaleString('es-ES')} h${remainderMinutes || seconds ? ` ${remainderMinutes} min${seconds ? ` ${seconds} s` : ''}` : ''}`;
  }
  return `${minutes.toLocaleString('es-ES')} min${seconds || remainder ? ` ${seconds}${decimalMilliseconds(remainder)} s` : ''}`;
}
